import { Component, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subject, merge, forkJoin } from 'rxjs';
import { takeUntil, flatMap, map, tap, filter } from 'rxjs/operators';
import { TenantService } from '../services/tenant.service';
import { StreamService } from '../services/stream.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'create-rss-source',
    template: `
        <mat-card [formGroup]="form">
            <mat-card-header>
                <h2>Create RSS Source</h2>
            </mat-card-header>
            <mat-card-content style="padding: 25px;">
                <div fxLayout="column" style="height: 100%">
                    <mat-form-field fxFlex>
                        <input matInput placeholder="RSS Url" formControlName="rssUrl">
                        <mat-error *ngIf="form.get('rssUrl').hasError('required')">Url is required.</mat-error>    
                    </mat-form-field>
                    <mat-checkbox formControlName="isAuthenticated">Authenticated Feed</mat-checkbox>
                    <mat-form-field fxFlex *ngIf="form.get('isAuthenticated').value">
                        <input matInput placeholder="User name" formControlName="rssUsername">
                        <mat-error *ngIf="form.get('rssUsername').hasError('required')">User name is required.</mat-error>    
                    </mat-form-field>
                    <mat-form-field fxFlex *ngIf="form.get('isAuthenticated').value">
                        <input matInput placeholder="Password" formControlName="rssPassword">
                    </mat-form-field>
                </div>
            </mat-card-content>
            <mat-card-actions>
                <button mat-raised-button [color]="streamConfig ? undefined : 'primary'" [disabled]="form.invalid" (click)="search()">Search</button>
                <button *ngIf="streamConfig" mat-raised-button color="primary" (click)="saveStream()">Save</button>
            </mat-card-actions>
        </mat-card>
    `
})
export class CreateRssSourceComponent implements OnDestroy {
    private cleanupTime = new Subject<void>();
    form: FormGroup;
    @Output() resultsFetched = new EventEmitter<any>();
    @Output() matchFound = new EventEmitter<any>();
    @Output() save = new EventEmitter<any>();
    streamConfig: { title: string; description: string; rssLink: string; };

    constructor(fb: FormBuilder,
                private snackbar: MatSnackBar,
                private streamService: StreamService, 
                private tenantService: TenantService) {
        this.form = fb.group({
            rssUrl: ['', Validators.required],
            isAuthenticated: [false],
            rssUsername: [],
            rssPassword: []
        });

        merge(...['rssUrl', 'isAuthenticated', 'rssUsername', 'rssPassword'].map(key => this.form.get(key).valueChanges))
        .pipe(takeUntil(this.cleanupTime))
        .subscribe(() => {
            this.streamConfig = undefined;
            this.resultsFetched.emit();
        });

        this.form.get('isAuthenticated').valueChanges.pipe(takeUntil(this.cleanupTime))
        .subscribe(val => {
            const rssUsername = this.form.get('rssUsername');
            rssUsername.setValidators(val ? [Validators.required] : []);
            rssUsername.updateValueAndValidity();
        });
    }

    search() {
        const primary = this.form.get('rssUrl').value;
        const secondary = this.cleanUrl(primary);
        let url;
        
        forkJoin([
            this.streamService.getTenantStreams({includeCatalogs: true, term: primary, page: 0, rows: 40}),
            this.streamService.getTenantStreams({includeCatalogs: true, term: secondary, page: 0, rows: 40})
        ])
        .pipe(
            map(([res1, res2]) => {
                return res1.content.find(stream => this.cleanUrl(stream.settings.uri) === this.cleanUrl(primary)) ||
                       res2.content.find(stream => this.cleanUrl(stream.settings.uri) === this.cleanUrl(secondary));
            }),
            tap(matched => {
                if (matched) {
                    this.matchFound.emit(matched);
                }
            }),
            filter(matched => !matched),
            flatMap(() => this.tenantService.getCurrentTenant()),
            flatMap(tenant => {
                const proxyId = tenant.defaultProxyId;
                url = this.form.get('rssUrl').value;
                url = url.indexOf('http') !== 0 ? 'http://' + url : url;
                const isAuthenticated = this.form.get('isAuthenticated').value;
                const config = {
                        uri: url,
                        username: isAuthenticated ? this.form.get('rssUsername').value : undefined,
                        password: isAuthenticated ? this.form.get('rssPassword').value : undefined
                };
                return this.streamService.getRssPreview(config, proxyId);
            })
        )
        .subscribe(
            results => {
                this.streamConfig = {
                    title: results.title ? results.title : url,
                    description: results.description,
                    rssLink: url
                };
                this.resultsFetched.emit(results);
            },
            err => {
                const errMessage = err.status === 401 ?
                    'Unauthorized. Username and password may be incorrect or missing.' :
                    'Unable to read RSS feed. Please ensure your url is a valid RSS feed.';
                this.snackbar.open(errMessage, 'OK',  { duration: 5000 });
            }
        )
    }

    cleanUrl(url) {
        let val = url;
        val = val.indexOf('//') !== -1 ? val.substring(val.indexOf('//') + 2) : val;
        return val.endsWith('/') ? val.substring(0, val.length -1) : val; 
    }

    saveStream() {
        const isAuthenticated = this.form.get('isAuthenticated').value;
        this.save.emit({
            title: this.streamConfig.title,
            description: this.streamConfig.description,
            settings: {
                uri: this.streamConfig.rssLink,
                username: isAuthenticated ? this.form.get('rssUsername').value : undefined,
                password: isAuthenticated ? this.form.get('rssPassword').value : undefined
            },
            openForReading: true,
            type: 'RSS',
            emailPostingEnabled: false
        });
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}